/**
 * Admin tab: list all pending personal avatars across communities and
 * approve / reject them. Approved avatars become the user's public avatar
 * across the community. Rejecting resets the user's attempts counter.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COMMUNITY_LABEL = {
  nuts: '🥜 NUTS',
  caps: '🧢 CAPS',
  shark: '🦈 SHARK',
};

export const PendingAvatarsAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/zona-pro/personal-avatars/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(r.data?.pending || []);
    } catch (e) {
      toast.error('No s\'han pogut carregar els avatars pendents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (item, action) => {
    setBusyId(`${item.community}/${item.member_id}`);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/zona-pro/personal-avatars/${item.community}/${item.member_id}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(action === 'approve' ? 'Avatar aprovat' : 'Avatar rebutjat');
      setItems((arr) => arr.filter((x) => !(x.community === item.community && x.member_id === item.member_id)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-500">Carregant…</div>;
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-slate-500">
          <div className="text-4xl mb-3">🖼️</div>
          <p className="font-medium">No hi ha cap avatar pendent</p>
          <p className="text-sm mt-1">Quan un membre generi un avatar personal, apareixerà aquí per a la teva aprovació.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="pending-avatars-admin">
      <div className="text-sm text-slate-600">
        {items.length} avatar{items.length === 1 ? '' : 's'} pendent{items.length === 1 ? '' : 's'} d&apos;aprovació
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => {
          const id = `${it.community}/${it.member_id}`;
          const busy = busyId === id;
          return (
            <Card key={id} data-testid={`pending-avatar-${it.community}-${it.member_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <img
                    src={`${API_URL}${it.pending_url}`}
                    alt={it.nickname}
                    className="w-28 h-28 rounded-xl object-cover border border-amber-200"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-amber-700">
                      {COMMUNITY_LABEL[it.community] || it.community}
                    </div>
                    <div className="mt-1 font-medium text-lg">{it.nickname}</div>
                    <div className="text-xs text-slate-500 truncate">{it.email}</div>
                    {it.full_name && <div className="text-xs text-slate-500">{it.full_name}</div>}
                    <div className="text-[11px] text-slate-400 mt-1">Intent {it.attempts}/2</div>
                    {it.current_avatar_url && (
                      <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
                        <span>Actual:</span>
                        <img src={it.current_avatar_url.startsWith('http') ? it.current_avatar_url : `${API_URL}${it.current_avatar_url}`} alt="current" className="w-7 h-7 rounded-full object-cover" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => act(it, 'reject')}
                    className="text-red-600 hover:bg-red-50"
                    data-testid={`reject-avatar-${it.community}-${it.member_id}`}
                  >
                    Rebutjar
                  </Button>
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => act(it, 'approve')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`approve-avatar-${it.community}-${it.member_id}`}
                  >
                    {busy ? '…' : '✓ Aprovar'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PendingAvatarsAdmin;
